import { forwardRef } from "react";

import * as RadixRadioGroup from "@radix-ui/react-radio-group";

import { cn } from "../index";

/* -------------------------------------------------------------------------------------------------
 * RadioGroup
 * -----------------------------------------------------------------------------------------------*/

type RadioGroupProps = RadixRadioGroup.RadioGroupProps;

const Root = forwardRef<HTMLDivElement, RadioGroupProps>(({ orientation = "horizontal", className, ...props }, ref) => (
  <RadixRadioGroup.Root
    ref={ref}
    className={cn("flex gap-[10px] text-base italic data-[orientation=vertical]:flex-col", className)}
    orientation={orientation}
    {...props}
  />
));

Root.displayName = "RadioGroup";

/* -------------------------------------------------------------------------------------------------
 * Item
 * -----------------------------------------------------------------------------------------------*/

type ItemProps = RadixRadioGroup.RadioGroupItemProps;

const Item = forwardRef<HTMLLabelElement, ItemProps>(({ className, children, ...props }, ref) => (
  <label ref={ref} className={cn("flex items-center gap-[5px]", className)}>
    <RadixRadioGroup.Item
      className={cn(
        `box-border grid h-[12px] w-[12px] shrink-0 cursor-pointer appearance-none place-content-center rounded-full border-[1px] border-primary-darker-gray`,
        "ease-in-out transition-[border-color,transform] duration-[120ms]",
        "data-[state=checked]:border-primary-blue",
        "disabled:pointer-events-none disabled:cursor-default disabled:data-[state=checked]:border-primary-darker-gray",
      )}
      {...props}
    >
      <RadixRadioGroup.Indicator
        className={cn(
          "m-[3px] h-[6px] w-[6px] scale-0 rounded-full",
          "ease-in-out transition-transform duration-[120ms]",
          "shadow-[inset_12px_12px_var(--primary-blue-color)]",
          "data-[state=checked]:scale-100",
          "data-[disabled]:shadow-[inset_0_0_0_6px_var(--color-primary-darker-gray)]",
        )}
      />
    </RadixRadioGroup.Item>
    {children}
  </label>
));

Item.displayName = "RadioGroupItem";

export { Root, Item };
export type { RadioGroupProps };
